import type { ReactNode } from 'react';
import { Card, CardContent } from 'components/ui/card';
import { TruncateTooltip } from 'hooks/use-truncate-tooltip';
import { cn } from 'lib/utils';

/** A usage stat card: title, big value with its unit, optional detail rows, and a footer note. */
export function UsageCreditCard({
  title,
  value,
  unit,
  children,
  footer,
  footerLines,
  action,
  valueClassName,
}: {
  title: string;
  value: string;
  /** Recolours the big value, e.g. red once nothing is left. */
  valueClassName?: string;
  unit: string;
  children?: ReactNode;
  footer: string;
  /** 2 lets a long footer wrap once before it is cut off. */
  footerLines?: 1 | 2;
  /** Shown at the right of the title, e.g. a Top up button. */
  action?: ReactNode;
}) {
  return (
    <Card className="min-w-0 rounded-section border-border/70 shadow-none">
      <CardContent className="flex h-full flex-col gap-2.5 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          {action}
        </div>
        <div className="flex items-baseline gap-2">
          <span
            className={cn(
              'shrink-0 text-2xl font-medium leading-heading text-foreground sm:text-3xl sm:leading-heading',
              valueClassName
            )}
          >
            {value}
          </span>
          {/* Narrow cards (e.g. the Teams drawer) cut the unit off instead of wrapping it. */}
          <div className="min-w-0 flex-1">
            <TruncateTooltip text={unit} className="text-sm text-muted-foreground" />
          </div>
        </div>
        {children}
        <div className="mt-auto border-t border-border pt-3">
          <TruncateTooltip
            text={footer}
            lines={footerLines}
            className="text-xs text-muted-foreground"
          />
        </div>
      </CardContent>
    </Card>
  );
}
